/**
 * The session ended on the server (1.2.4): the idle timeout, a sign-out in another tab, an administrator ending it.
 * Any 401 that says so sends a signed-in app back to sign-in, with why: the idle timeout keeps its inactivity
 * message, anything else says the session ended. Before 1.2.4 only SESSION_IDLE did, and the server sends that on
 * one request only (the first after the deadline, often the live stream's, which cannot read it), so the app stayed
 * on screen with "Live updates are disconnected" until a refresh.
 */
import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from './client';
import { queryKeys } from './queryKeys';

const SIGNED_OUT_MESSAGE_KEY = 'prism.signedOutMessage';
export const SESSION_ENDED_MESSAGE = 'Your session ended; sign in again';
/** Pages that work signed out: a 401 there is expected, never a reason to go anywhere. */
const PUBLIC_PAGES = /^\/(login|forgot-password|reset-password|setup|s\/)/;

export const idleMessage = (minutes: number) => `Signed out after ${minutes} minute${minutes === 1 ? '' : 's'} of inactivity`;

/** Shown once on the sign-in page after an automatic sign-out. */
export function takeSignedOutMessage(): string | null {
  try {
    const m = sessionStorage.getItem(SIGNED_OUT_MESSAGE_KEY);
    if (m) sessionStorage.removeItem(SIGNED_OUT_MESSAGE_KEY);
    return m;
  } catch {
    return null;
  }
}

export function rememberSignedOutMessage(message: string): void {
  try { sessionStorage.setItem(SIGNED_OUT_MESSAGE_KEY, message); } catch { /* private mode */ }
}

/** The idle clock the IdleGuard keeps (its last activity and the timeout), so a plain 401 after the deadline still says why. */
let idleClock: { base: () => number; minutes: number } | null = null;
export function setIdleClock(clock: typeof idleClock): void { idleClock = clock; }

/** The idle timeout in minutes when this tab is past its idle deadline, else null. */
export function pastIdleDeadline(now = Date.now()): number | null {
  if (!idleClock || idleClock.minutes <= 0) return null;
  return now >= idleClock.base() + idleClock.minutes * 60_000 - 1000 ? idleClock.minutes : null;
}

/**
 * What the sign-in page says after this error, or null when the error is no reason to go there. SESSION_IDLE always
 * is (also on the first request of a fresh page load); UNAUTHENTICATED only for an app that was signed in, and not on
 * a page that works signed out (so the sign-in page never loops on its own 401s).
 */
export function signInNotice(err: unknown, at: { signedIn: boolean; path: string; idleMinutesPast?: number | null }): string | null {
  if (!(err instanceof ApiError) || err.status !== 401) return null;
  if (err.code === 'SESSION_IDLE') return err.message;
  if (err.code !== 'UNAUTHENTICATED' || !at.signedIn || PUBLIC_PAGES.test(at.path)) return null;
  return at.idleMinutesPast ? idleMessage(at.idleMinutesPast) : SESSION_ENDED_MESSAGE;
}

/**
 * Back to sign-in with a message: every cached answer is dropped and the signed-in person is asked for again, which
 * answers 401 and shows the sign-in page. With nobody cached any more, the 401s that follow change nothing.
 */
export function endSession(qc: QueryClient, message: string): void {
  rememberSignedOutMessage(message);
  qc.removeQueries({ predicate: (q) => q.queryKey[0] !== queryKeys.me()[0] });
  void qc.resetQueries({ queryKey: queryKeys.me() });
}

/** For every failed query and mutation (and the live stream's session check): a 401 that ends the session goes to sign-in. */
export function onSessionError(qc: QueryClient, err: unknown): boolean {
  const message = signInNotice(err, {
    signedIn: qc.getQueryData(queryKeys.me()) !== undefined, path: window.location.pathname, idleMinutesPast: pastIdleDeadline(),
  });
  if (message === null) return false;
  endSession(qc, message);
  return true;
}
